import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Mic, MicOff, Loader2, Sparkles, Wand2 } from 'lucide-react';
import { useBackend } from '@/services/backend';
import type { WhisperConfig } from '@/services/backend/types';
import { ENHANCE_MODEL_DEFAULT_URL, ENHANCE_MODEL_DEFAULT_NAME } from '@/stores/settingsStore';
import { useAuthStore } from '@/stores/authStore';
import { useInputHistoryStore } from '@/stores/inputHistoryStore';
import { useTranscriptDraftStore } from '@/stores/transcriptDraftStore';
import { useProjectStore } from '@/stores/projectStore';
import { useTerminalStore } from '@/stores/terminalStore';
import { useTranscriptionQueueStore } from '@/stores/transcriptionQueueStore';
import { useNavigate } from 'react-router-dom';
import { showToast } from '@/components/ui/Toast';
import { logger } from '@/lib/logger';
import {
  record as veRecord,
  transcribe as veTranscribe,
  live as veLive,
  type Recording as VeRecording,
  type LiveSession as VeLiveSession,
} from '@/lib/voice-engine';
import { writeTextToPty, uint8ToBase64 } from '@/lib/base64';

const TAG = 'TerminalMic';
const MAX_DURATION_MS = 1_800_000; // 30 minutes

// Recover a leftover in-flight transcript only ONCE per app session, even though
// several mic buttons mount (raw + enhanced, per terminal).
let draftRecoveredThisSession = false;

type MicState = 'idle' | 'recording' | 'transcribing' | 'error';

/** Per-recording context captured at start. Lives in a ref so the closures
 *  that handle MediaRecorder onstop / live chunks can paste into the right
 *  terminal even after the user has switched tabs or kicked off another
 *  recording. Tracked PER RECORDING, never globally — concurrent
 *  transcriptions each carry their own ctx in their closure chain. */
interface RecordingContext {
  /** Terminal session to paste the final transcript into. Captured at
   *  start so user can switch terminals during recording without
   *  redirecting the paste. */
  targetSessionId: number | null;
  /** Title of that terminal tab at record-start, for the completion toast —
   *  so a user running several agent sessions in one project sees WHICH tab
   *  the transcript landed in. */
  targetSessionTitle: string | null;
  projectName: string;
  /** Project path — used to select the right terminal tab on toast-click. */
  projectPath: string;
  /** Project id for the deep-link in the completion toast. */
  projectId: string | null;
}

interface TerminalMicButtonProps {
  activeSessionId: number | null;
  projectPath: string;
  /** When true, the raw transcript is piped through the local AI for clean-up
   *  before being pasted (the "✨ enhanced mic"). Default false = raw transcript. */
  enhanced?: boolean;
}

/** Check if a WhisperConfig represents a configured provider */
function isConfigured(config: WhisperConfig): boolean {
  // Optional chaining is load-bearing: over WebRTC the host's whisper config can
  // arrive with a field missing (e.g. an older host that doesn't send
  // voiceEngineToken), and `undefined.length` would throw during render — which,
  // because this button lives in the terminal toolbar, took down the entire
  // remote terminal panel (the IDEA-006 "Error rendering component" crash).
  if (config.provider === 'openai') return (config.openaiApiKey?.length ?? 0) > 0;
  if (config.provider === 'voice-engine') return (config.voiceEngineToken?.length ?? 0) > 0;
  return false;
}

const DEFAULT_WHISPER_CONFIG: WhisperConfig = {
  provider: 'none',
  openaiApiKey: '',
  transcriptionMode: 'live',
  voiceEngineUrl: 'https://transcribe.unveiledsoftwaresolutions.com',
  voiceEngineToken: '',
  voiceEngineModel: 'medium',
};

export default function TerminalMicButton({ activeSessionId, projectPath, enhanced = false }: TerminalMicButtonProps) {
  const backend = useBackend();
  const navigate = useNavigate();
  const [micSupported, setMicSupported] = useState(true);
  const [state, setState] = useState<MicState>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [whisperConfig, setWhisperConfig] = useState<WhisperConfig | null>(null);

  // Batch-mode refs (existing MediaRecorder path)
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Cloud-batch ref. When provider === 'voice-engine' we capture audio
  // through @unveiled/voice-engine's record() instead of MediaRecorder
  // and ship the resulting Float32Array via its transcribe() — keeps the
  // upload format identical to what the cluster's Symphonia path expects
  // (proper RIFF-WAV with filename), which the old WAV-bytes-through-Rust
  // path could and did get wrong under some browsers.
  const cloudRecordingRef = useRef<VeRecording | null>(null);

  // LA-2 live-streaming ref. When provider === 'voice-engine' and the
  // user is in live transcription mode, we open a WebSocket session via
  // veLive() instead of buffering audio for batch upload. The session
  // streams PCM to the cluster's /v1/live endpoint and fires committed
  // (stable text) + provisional (wobbling tail) callbacks, which we
  // surface in the LiveTranscriptOverlay so the user sees text appear
  // as they speak.
  const liveCloudSessionRef = useRef<VeLiveSession | null>(null);
  /** Stop closure parked on the live session — analogous to __atelierStop
   *  on the batch ref so stopRecording() can just call it. */
  type LiveCloudHandle = { stop: () => Promise<void> };
  const liveCloudHandleRef = useRef<LiveCloudHandle | null>(null);
  const [liveCommitted, setLiveCommitted] = useState('');
  const [liveProvisional, setLiveProvisional] = useState('');
  // True from the moment the user hits stop on a live recording until the
  // server has flushed its tail and the final text is pasted. Keeps the
  // transcript overlay on screen through that brief flush so it doesn't look
  // like hitting stop threw away the end of what was said.
  const [finishing, setFinishing] = useState(false);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const maxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef(0);
  /** Context captured at the most recent recording-start. Used by
   *  startBatch/Live closures to anchor the paste/toast to whatever was
   *  active when the user pressed record — switching terminals mid-
   *  recording doesn't redirect the paste, and starting a new recording
   *  while a previous one's transcription is still in flight doesn't
   *  clobber its target (each recording's closure captures its own ctx). */
  const currentRecordingCtxRef = useRef<RecordingContext | null>(null);

  // Fetch whisper settings from backend (works for both Tauri and WebRTC)
  useEffect(() => {
    let cancelled = false;
    backend.transcription.getWhisperSettings().then((config) => {
      if (!cancelled) setWhisperConfig(config);
    }).catch((e) => {
      logger.warn(TAG, 'Failed to fetch whisper settings', e);
      if (!cancelled) setWhisperConfig(DEFAULT_WHISPER_CONFIG);
    });
    return () => { cancelled = true; };
  }, [backend]);

  // Capability check on mount
  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setMicSupported(false);
      return;
    }
    if (typeof MediaRecorder === 'undefined') {
      setMicSupported(false);
      return;
    }
  }, []);

  // Recover a transcript that was interrupted by a refresh (e.g. a UI
  // hot-reload) mid-dictation. Saved incrementally to the persisted draft
  // store; on load we move it into the input history so it can be re-inserted,
  // and let the user know. Runs once per session across all mic instances.
  useEffect(() => {
    if (draftRecoveredThisSession) return;
    draftRecoveredThisSession = true;
    const draft = useTranscriptDraftStore.getState().draft;
    if (draft && draft.text.trim()) {
      useInputHistoryStore.getState().addEntry(draft.text, 'transcription', draft.projectPath);
      useTranscriptDraftStore.getState().clearDraft();
      showToast('Recovered an interrupted transcription — open the input-history button to re-insert it.', 'success');
    }
  }, []);

  // Warn before a user-initiated refresh/close while a recording or
  // transcription is in flight, so it isn't silently dropped.
  useEffect(() => {
    if (state !== 'recording' && state !== 'transcribing') return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [state]);

  // Hand focus back to the active terminal whenever recording wraps up
  // (clean stop, error, or empty transcription). The user just clicked the
  // mic to stop dictating — they want to be back in the terminal ready to
  // type / hit enter without an extra click.
  const prevStateRef = useRef<MicState>('idle');
  useEffect(() => {
    const prev = prevStateRef.current;
    prevStateRef.current = state;
    if ((prev === 'recording' || prev === 'transcribing') && state === 'idle') {
      useTerminalStore.getState().requestFocus();
    }
  }, [state]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop();
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
      // LA-2 session: cancel (don't await final) so the WS + mic are
      // released even if the user closes the tab mid-recording.
      if (liveCloudSessionRef.current) {
        void liveCloudSessionRef.current.cancel();
        liveCloudSessionRef.current = null;
        liveCloudHandleRef.current = null;
      }
      if (timerRef.current) clearInterval(timerRef.current);
      if (maxTimerRef.current) clearTimeout(maxTimerRef.current);
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, []);

  const showError = useCallback((msg: string) => {
    setState('error');
    setErrorMsg(msg);
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => {
      setState('idle');
      setErrorMsg('');
    }, 3000);
  }, []);

  // ─── Common: paste finished transcription into the captured terminal ───
  // Routes the result back to the terminal that was active when the user
  // pressed record (NOT whatever terminal happens to be active when the
  // transcription finishes — they're often different now that transcription
  // runs in the background after stop).
  const pasteTranscription = useCallback(async (text: string, providerLabel: string, ctx: RecordingContext, jobId: number) => {
    const queue = useTranscriptionQueueStore.getState();
    const transcribedText = text.trim();
    if (!transcribedText) { queue.dismiss(jobId); return; }

    // Save raw transcription to history immediately (crash-safety: it's persisted
    // before the enhancement round-trip that could fail). Once it's in the
    // persisted history the in-flight draft is redundant, so drop it. If
    // enhancement then succeeds we REPLACE this standalone raw entry with a single
    // paired entry (enhanced text + its raw origin) so the history stays
    // uncluttered and the panel can offer an Enhanced⇄Raw toggle on one row.
    const rawEntryId = useInputHistoryStore
      .getState()
      .addEntry(transcribedText, 'transcription', projectPath);
    useTranscriptDraftStore.getState().clearDraft();

    // What actually gets pasted, and whether to wrap it in bracketed paste.
    let toInsert: string;
    let bracketed = false;

    if (enhanced) {
      // Pipe the raw transcript through the local AI for clean-up, feeding it
      // project context so it can fix domain-specific mishearings and return a
      // clean, structured version of what the speaker meant.
      queue.beginEnhance(jobId);
      try {
        const cfg = await backend.transcription.getWhisperSettings();
        // Resolve the project THIS mic belongs to by its own path — NOT the
        // global currentProject, which can be stale or point at a different
        // project the user has since switched to (that fed the AI the wrong
        // project's context, e.g. FreePBX instead of Voyage).
        const { projects, currentProject } = useProjectStore.getState();
        const proj =
          (projectPath && projects.find((p) => p.root_path === projectPath)) ||
          currentProject;
        // Our BFF proxy (api.ateliercode.dev/api/llm) ONLY accepts the signed-in
        // user's Keycloak token, so for the proxy always send a fresh login token
        // — ignoring any stale key an older install may still have persisted for
        // the old gated endpoint. A genuine custom endpoint keeps its own key.
        const enhanceUrl = cfg.enhanceModelUrl || ENHANCE_MODEL_DEFAULT_URL;
        const enhanceKey = enhanceUrl.includes('/api/llm')
          ? ((await useAuthStore.getState().ensureFreshToken()) ?? '')
          : (cfg.enhanceModelApiKey?.trim() || '');
        const result = await backend.transcription.enhanceTranscription({
          text: transcribedText,
          baseUrl: enhanceUrl,
          model: cfg.enhanceModelName || ENHANCE_MODEL_DEFAULT_NAME,
          apiKey: enhanceKey,
          projectName: proj?.name ?? ctx.projectName,
          // Use the project's PRD content as domain context (capped so the
          // enhance prompt stays lean), trimmed of nulls.
          projectDescription: proj?.prd_content?.slice(0, 800) || undefined,
        });
        const cleaned = result.text.trim();
        if (cleaned) {
          // Label it so the receiving agent knows this is the AI-enhanced
          // ("smart") transcription — a cleaned, higher-fidelity version of what
          // was said — not a raw speech-to-text dump.
          toInsert = `[Smart transcription — AI-enhanced from voice]\n${cleaned}`;
          bracketed = true; // multi-line/bulleted → bracketed paste, don't auto-run
          // The enhanced result is a ready-to-send prompt, not a raw transcript.
          // Replace the standalone raw entry with ONE paired entry that carries
          // both the enhanced text and its raw origin, so the history panel shows
          // a single row with an Enhanced⇄Raw toggle instead of two loose rows.
          const store = useInputHistoryStore.getState();
          if (rawEntryId) store.removeEntry(rawEntryId);
          store.addEntry(cleaned, 'prompt', projectPath, transcribedText);
          queue.finish(jobId, cleaned.slice(0, 80));
        } else {
          toInsert = `[Transcribed via ${providerLabel}] "${transcribedText}"`;
          queue.finish(jobId, transcribedText.slice(0, 80));
        }
      } catch (e) {
        logger.warn(TAG, 'Enhancement failed, pasting raw transcript', e);
        const msg = e instanceof Error ? e.message : 'Enhancement failed';
        // A 401 from the BFF means the session lapsed between click and enhance
        // (or a genuine auth failure) — say so plainly and re-open the login,
        // rather than a cryptic "Local AI error (401)".
        const isAuthErr = /\b401\b|unauthor|authentication required/i.test(msg);
        if (isAuthErr) {
          showToast('Your session expired — please sign in again to use Smart transcription. Pasting raw transcript for now.', 'error');
          useAuthStore.getState().login();
        } else {
          showToast(`Enhancement failed in ${ctx.projectName}: ${msg}. Pasting raw transcript.`, 'error');
        }
        toInsert = `[Transcribed via ${providerLabel}] "${transcribedText}"`;
        // Keep the job visible as an error so the user can grab the message,
        // even though we still paste the raw transcript as a fallback.
        queue.fail(jobId, isAuthErr ? 'Session expired — sign in again' : msg);
      }
    } else {
      // Prefix with provider info so the agent knows this is speech-to-text
      // and can account for potential misheard/mistranscribed words.
      toInsert = `[Transcribed via ${providerLabel}] "${transcribedText}"`;
      queue.finish(jobId, transcribedText.slice(0, 80));
    }

    if (ctx.targetSessionId !== null) {
      await writeTextToPty(toInsert, ctx.targetSessionId, (id, data) => backend.terminal.write(id, data), bracketed);
    }

    // Toast routes the user back to the recording's project AND the exact
    // terminal tab the transcript landed in — even if they've since navigated
    // away or switched tabs — so they can hit Enter without re-finding it.
    const preview = toInsert.length > 60
      ? toInsert.replace(/\s+/g, ' ').slice(0, 57) + '...'
      : toInsert.replace(/\s+/g, ' ');
    // Where it went: "Project › Tab" when we know the tab, else just the project.
    const destination = ctx.targetSessionTitle
      ? `${ctx.projectName} › ${ctx.targetSessionTitle}`
      : ctx.projectName;
    showToast(
      `${enhanced ? 'Enhanced transcription' : 'Transcription'} ready in ${destination}: "${preview}"`,
      'success',
      ctx.projectId
        ? {
            action: {
              label: `Open ${destination}`,
              onClick: () => {
                // Select the exact terminal tab BEFORE navigating so the
                // TerminalTab panel mounts/re-renders already showing it —
                // not just the project's last-active terminal.
                if (ctx.targetSessionId != null) {
                  useTerminalStore.getState().setActiveTerminal(ctx.projectPath, ctx.targetSessionId);
                }
                navigate(`/workspace/${ctx.projectId}?tab=terminal&t=${Date.now()}`);
                useTerminalStore.getState().requestFocus();
              },
            },
          }
        : undefined,
    );
  }, [backend, projectPath, navigate, enhanced]);

  // ─── Batch mode: handle the full audio buffer at the end ────────────────
  // Pure background work — no React state mutation. Runs as a fire-and-
  // forget task after the user hits stop so they can immediately start
  // another recording without waiting on this one's pipeline.
  const handleBatchTranscription = useCallback(async (blob: Blob, ctx: RecordingContext) => {
    const queue = useTranscriptionQueueStore.getState();
    const jobId = queue.start(ctx.projectName, enhanced);
    try {
      // Fetch fresh settings at transcription time (covers settings changes mid-session)
      const config = await backend.transcription.getWhisperSettings();
      let result: { text: string };

      let providerLabel: string;
      if (config.provider === 'openai') {
        const buffer = await blob.arrayBuffer();
        // Send audio as a base64 string (one encode) instead of a JSON number
        // array (one JSON element per byte) — far smaller frame, decoded once
        // server-side.
        const audioData = uint8ToBase64(new Uint8Array(buffer));
        result = await backend.transcription.transcribeOpenAI(audioData, config.openaiApiKey);
        providerLabel = 'OpenAI Whisper';
      } else {
        // 'voice-engine' shouldn't reach this batch path anymore — its
        // recordings go through startCloudBatchRecording / handleCloudBatch
        // which uses @unveiled/voice-engine end-to-end. Bail loudly so we
        // notice if some new code path routes a voice-engine recording
        // through the MediaRecorder branch.
        logger.warn(TAG, `handleBatchTranscription: unexpected provider ${config.provider}`);
        return;
      }

      await pasteTranscription(result.text, providerLabel, ctx, jobId);
    } catch (e) {
      logger.warn(TAG, 'Transcription failed', e);
      const msg = e instanceof Error ? e.message : 'Transcription failed';
      showToast(`Transcription failed in ${ctx.projectName}: ${msg}`, 'error');
      queue.fail(jobId, msg);
    } finally {
      queue.settle(jobId);
    }
  }, [backend, pasteTranscription]);

  // ─── Batch mode: start MediaRecorder, buffer everything until stop ──────
  // `ctx` is captured by the onstop closure so the post-stop transcribe
  // pipeline always pastes into the right terminal (the one that was active
  // at start), even if the user has switched terminals or kicked off
  // another recording in the meantime.
  const startBatchRecording = useCallback(async (ctx: RecordingContext) => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    streamRef.current = stream;

    const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : undefined;
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    recorderRef.current = recorder;
    chunksRef.current = [];
    // Capture the per-recording chunk array — chunksRef is shared and would
    // be reset by the next start(), so we snapshot it here.
    const ourChunks = chunksRef.current;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) ourChunks.push(e.data);
    };

    recorder.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      // Don't touch streamRef — a newer recording may have already taken
      // over the slot. Same applies to recorderRef + chunksRef.
      const blob = new Blob(ourChunks, { type: mimeType || 'audio/webm' });
      if (blob.size > 0) {
        // Fire-and-forget — runs on the queue alongside any other in-flight
        // transcriptions. handleBatchTranscription does its own try/finally
        // around queue.finish.
        void handleBatchTranscription(blob, ctx);
      }
    };

    recorder.start();
  }, [handleBatchTranscription]);

  // ─── Cloud batch: full lifecycle through @unveiled/voice-engine ────────
  // record() captures Float32 PCM via AudioWorklet, downsamples to 16 kHz,
  // and stop() returns a Float32Array. transcribe() wraps it in a proper
  // RIFF-WAV with filename, posts multipart to the cluster, awaits the
  // job inline (or polls if it spills past the inline_wait), and returns
  // the text. No WAV bytes through Rust, no MediaRecorder, no decode
  // round-trip — same path the upstream lib's tests exercise.
  const startCloudBatchRecording = useCallback(async (ctx: RecordingContext, config: WhisperConfig) => {
    const recording = await veRecord();
    cloudRecordingRef.current = recording;

    // Snapshot the recording handle for the post-stop closure so a
    // follow-on recording starting before this one's transcription
    // finishes doesn't yank our samples out from under us.
    const ourRecording = recording;

    // Snapshot the cloud config — settings the user changes between
    // start and stop must NOT change which cluster this recording goes to.
    const baseUrl = config.voiceEngineUrl;
    const token = config.voiceEngineToken;

    // Stash the handle on a ref so stopRecording can call .stop() on it.
    // The actual transcribe call hangs off that .stop() resolving — we
    // wire it up here so the queue+toast pipeline runs identically to
    // the MediaRecorder path.
    //
    // We deliberately do NOT pass `model` or `language`. The cluster's
    // `ateliercode` profile (configured in the cluster's
    // VOICE_PROFILES_JSON) owns model (medium), language (en), and the
    // dev-tooling initial_prompt. Sending an explicit `model=...` would
    // override the profile's choice — and previously, when this was
    // wired, a stale settings value (`base`) overrode `medium` and the
    // user got dramatically worse transcripts without realising it
    // (cluster log timestamps 2026-05-05T01:20:58Z–01:21:43Z show this
    // happening before the migration to @unveiled/voice-engine landed).
    // Profile-only is the failure-proof path.
    const onStopCb = async () => {
      const queue = useTranscriptionQueueStore.getState();
      const jobId = queue.start(ctx.projectName, enhanced);
      try {
        const samples = await ourRecording.stop();
        if (samples.length === 0) return;
        const result = await veTranscribe(samples, {
          baseUrl,
          token,
          profile: 'ateliercode',
        });
        await pasteTranscription(result.text, `Cloud (${result.model ?? 'profile'})`, ctx, jobId);
      } catch (e) {
        logger.warn(TAG, 'Cloud transcription failed', e);
        const msg = e instanceof Error ? e.message : 'Transcription failed';
        showToast(`Transcription failed in ${ctx.projectName}: ${msg}`, 'error');
        queue.fail(jobId, msg);
      } finally {
        queue.settle(jobId);
      }
    };
    // Park the close-over closure on the recording object so stopRecording
    // can grab + invoke it without needing to know the ctx.
    (ourRecording as VeRecording & { __atelierStop?: () => Promise<void> }).__atelierStop = onStopCb;
  }, [pasteTranscription]);

  // ─── Cloud LIVE (LA-2): stream PCM to /v1/live, render wobble-then-settle ─
  // Replaces the cloud-batch path when provider === 'voice-engine' and
  // transcriptionMode === 'live'. The server uses LocalAgreement-2 to emit
  // `committed` (locked-in text) + `provisional` (still-revising tail)
  // events, which we surface in the floating overlay. On stop() the lib
  // resolves to the full concatenated transcript that we paste into the
  // captured terminal, same as every other path.
  const startLiveCloudRecording = useCallback(async (ctx: RecordingContext, config: WhisperConfig) => {
    setLiveCommitted('');
    setLiveProvisional('');

    // Same profile-only argument set as the batch cloud path — the cluster's
    // `ateliercode` profile owns model / language / initial prompt. See the
    // big comment in startCloudBatchRecording for why we don't pass model.
    const baseUrl = config.voiceEngineUrl;
    const token = config.voiceEngineToken;

    const session = await veLive({
      baseUrl,
      token,
      profile: 'ateliercode',
      strategy: 'local_agreement',
      onCommitted: () => {
        // The lib accumulates committed text — pull the full snapshot
        // each time instead of trying to splice incrementally.
        const committed = session.committedText();
        setLiveCommitted(committed);
        setLiveProvisional(session.provisionalText());
        // Persist the growing transcript so a refresh mid-dictation can recover it.
        useTranscriptDraftStore.getState().setDraft(
          `${committed} ${session.provisionalText()}`.trim(),
          projectPath,
        );
      },
      onProvisional: ({ text }) => {
        setLiveProvisional(text);
        useTranscriptDraftStore.getState().setDraft(
          `${session.committedText()} ${text}`.trim(),
          projectPath,
        );
      },
      onError: (msg) => {
        logger.warn(TAG, 'Live cloud error', msg);
      },
    });

    liveCloudSessionRef.current = session;

    // Park the stop closure so stopRecording can fire it without knowing ctx.
    const handle: LiveCloudHandle = {
      stop: async () => {
        const queue = useTranscriptionQueueStore.getState();
        const jobId = queue.start(ctx.projectName, enhanced);
        // Keep the overlay up (now labelled "Finishing…") while the server
        // flushes its tail, so stop doesn't look like it dropped the ending.
        setFinishing(true);
        try {
          const fullText = await session.stop();
          // Clear the overlay before pasting so the user sees the same
          // text materialise in the terminal rather than two copies.
          setLiveCommitted('');
          setLiveProvisional('');
          if (fullText.trim()) {
            await pasteTranscription(fullText, 'Cloud (live)', ctx, jobId);
          }
        } catch (e) {
          logger.warn(TAG, 'Cloud live transcription failed', e);
          const msg = e instanceof Error ? e.message : 'Transcription failed';
          showToast(`Transcription failed in ${ctx.projectName}: ${msg}`, 'error');
          queue.fail(jobId, msg);
        } finally {
          setFinishing(false);
          liveCloudSessionRef.current = null;
          liveCloudHandleRef.current = null;
          queue.settle(jobId);
        }
      },
    };
    liveCloudHandleRef.current = handle;
  }, [pasteTranscription]);

  // ─── Top-level start: pick mode based on settings, fall back if needed ──
  const startRecording = useCallback(async () => {
    try {
      // Fetch fresh settings at recording time so changes mid-session apply.
      const config = await backend.transcription.getWhisperSettings();
      if (!isConfigured(config)) {
        showError('Voice input not configured');
        return;
      }

      // Capture context NOW (target session, project name, project id) so the
      // background transcription can paste into the right terminal even if
      // the user switches tabs / projects mid-recording or starts another
      // recording before this one's transcription completes.
      //
      // Resolve the project by THIS mic's path (the terminal's project), not the
      // global currentProject — otherwise switching projects mid/after recording
      // mislabels the toast and, for enhanced transcription, feeds the AI the
      // wrong project's context.
      const { projects: allProjects, currentProject: liveCurrent } = useProjectStore.getState();
      const ownProject = (projectPath && allProjects.find((p) => p.root_path === projectPath)) || liveCurrent;
      // Grab the tab title so the completion toast can name WHICH terminal the
      // transcript went to — important when the user has several agent sessions
      // open in one project.
      const targetSessionTitle = activeSessionId != null
        ? (useTerminalStore.getState().sessions.get(activeSessionId)?.title ?? null)
        : null;
      const ctx: RecordingContext = {
        targetSessionId: activeSessionId,
        targetSessionTitle,
        projectPath,
        projectName: ownProject?.name
          || projectPath.split(/[/\\]/).pop()
          || 'Unknown',
        projectId: ownProject?.id ?? null,
      };
      currentRecordingCtxRef.current = ctx;

      // Two remaining capture paths:
      //  - voice-engine + live: LA-2 streaming over WS (startLiveCloudRecording)
      //  - everything else: batch (full audio buffered, transcribed on stop).
      //    voice-engine batch goes through @unveiled/voice-engine; OpenAI goes
      //    through the MediaRecorder path (startBatchRecording).
      const wantsLive = config.transcriptionMode === 'live';
      const liveCloud = wantsLive && config.provider === 'voice-engine';

      let started = false;
      if (liveCloud) {
        try {
          await startLiveCloudRecording(ctx, config);
          started = true;
        } catch (e) {
          logger.warn(TAG, 'LA-2 init failed, falling back to cloud batch', e);
          showToast('Live transcription unavailable — falling back to batch mode', 'success');
        }
      }
      if (!started) {
        if (config.provider === 'voice-engine') {
          await startCloudBatchRecording(ctx, config);
        } else {
          await startBatchRecording(ctx);
        }
      }

      setState('recording');
      setElapsed(0);
      startTimeRef.current = Date.now();

      timerRef.current = setInterval(() => {
        setElapsed(Date.now() - startTimeRef.current);
      }, 1000);

      maxTimerRef.current = setTimeout(() => {
        // Hit the max — stop whichever mode is running.
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        stopRecording();
      }, MAX_DURATION_MS);
    } catch (e) {
      logger.warn(TAG, 'Failed to start recording', e);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      const friendly = e instanceof DOMException && e.name === 'NotAllowedError'
        ? 'Microphone access denied'
        : 'Failed to start recording';
      showError(friendly);
      // Also surface the real error in a toast — the error state only shows
      // `friendly` in the button tooltip, which is easy to miss, so a silent
      // failure looks like "mic flashed red then went back to ready" with no
      // explanation. The detail (e.g. NotAllowedError on WebKitGTK, or an
      // AudioWorklet/getUserMedia message) is what actually tells us why.
      const detail = e instanceof Error ? `${e.name}: ${e.message}` : String(e);
      showToast(`Voice input failed: ${detail}`, 'error');
    }
  }, [backend, projectPath, activeSessionId, showError, startLiveCloudRecording, startBatchRecording, startCloudBatchRecording]);

  const stopRecording = useCallback(() => {
    // Clear timers immediately so we don't double-fire.
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (maxTimerRef.current) {
      clearTimeout(maxTimerRef.current);
      maxTimerRef.current = null;
    }

    // Clear the captured-context ref so the next startRecording() can
    // capture fresh ctx without racing the outgoing transcription. The
    // background pipelines already closed over their own ctx at start-time.
    currentRecordingCtxRef.current = null;

    // Free the button immediately — the actual transcription pipeline runs
    // in the background (handleBatchTranscription / cloud stop closures),
    // tracked in transcriptionQueueStore. The user can start a new
    // recording right away without waiting on this one.
    setState('idle');

    if (liveCloudHandleRef.current) {
      // Cloud live (LA-2): invoke the parked stop closure that
      // startLiveCloudRecording wired up. It awaits the WS final-flush,
      // tears down mic + WS, pastes the full transcript.
      const handle = liveCloudHandleRef.current;
      liveCloudHandleRef.current = null;
      void handle.stop();
    } else if (cloudRecordingRef.current) {
      // Cloud batch: invoke the parked stop closure that startCloud-
      // BatchRecording wired up. It owns the queue.start/finish + the
      // cluster transcribe + the paste — runs in the background.
      const rec = cloudRecordingRef.current as VeRecording & { __atelierStop?: () => Promise<void> };
      cloudRecordingRef.current = null;
      if (rec.__atelierStop) void rec.__atelierStop();
    } else {
      // OpenAI batch: ask the MediaRecorder to stop. Its onstop closure
      // already captured ctx at start-time, so the post-stop transcribe
      // pipeline routes correctly even though we've cleared the ref.
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.stop();
      }
    }
  }, []);

  const handleClick = useCallback(async () => {
    if (state === 'recording') {
      stopRecording();
      return;
    }
    if (state !== 'idle') return;
    // Enhanced ("Smart") transcription posts to the authenticated BFF (/api/llm),
    // which needs a valid Keycloak session. If the user is logged out or their
    // session has expired, don't start a recording that will 401 at the end —
    // surface a clear message and open the login flow so they sign in FIRST.
    // (A genuine custom enhance endpoint isn't gated on the login.)
    if (enhanced) {
      try {
        const cfg = await backend.transcription.getWhisperSettings();
        const enhanceUrl = cfg.enhanceModelUrl || ENHANCE_MODEL_DEFAULT_URL;
        if (enhanceUrl.includes('/api/llm')) {
          const auth = useAuthStore.getState();
          const token = await auth.ensureFreshToken();
          if (!token) {
            showToast(
              auth.isAuthenticated
                ? 'Your session expired — please sign in again to use Smart transcription.'
                : 'Sign in to use Smart transcription (AI enhancement).',
              'error',
            );
            auth.login();
            return;
          }
        }
      } catch (e) {
        // Don't block recording on a settings/auth pre-check hiccup — the enhance
        // step below still has its own auth-aware fallback.
        logger.warn(TAG, 'Auth pre-check for enhanced mic failed', e);
      }
    }
    startRecording();
  }, [state, enhanced, stopRecording, startRecording, backend]);

  // Don't render if mic not supported
  if (!micSupported) return null;

  // Format elapsed time as M:SS
  const formatDuration = (ms: number) => {
    const totalSec = Math.floor(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  // Determine button appearance
  const notConfigured = !whisperConfig || whisperConfig.provider === 'none' || !isConfigured(whisperConfig);
  const noTerminal = activeSessionId === null;
  const disabled = notConfigured || noTerminal || state === 'transcribing';

  let icon: React.ReactNode;
  let className = 'relative flex items-center gap-1 h-6 px-1.5 rounded transition-colors ';
  let title = '';

  switch (state) {
    case 'error':
      icon = <MicOff className="w-3.5 h-3.5" />;
      className += 'text-red-500 cursor-default';
      title = errorMsg;
      break;
    case 'transcribing':
      icon = <Loader2 className="w-3.5 h-3.5 animate-spin" />;
      className += 'text-amber-400 cursor-default';
      title = 'Transcribing...';
      break;
    case 'recording':
      icon = <Mic className="w-3.5 h-3.5" />;
      className += 'text-red-400 animate-pulse cursor-pointer';
      title = 'Recording... click to stop';
      break;
    default: // idle
      if (notConfigured) {
        icon = enhanced ? <Wand2 className="w-3.5 h-3.5" /> : <MicOff className="w-3.5 h-3.5" />;
        className += 'text-stone-600 cursor-default';
        title = 'Configure voice input in Settings';
      } else if (noTerminal) {
        icon = enhanced ? <Wand2 className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />;
        className += 'text-stone-600 opacity-50 cursor-default';
        title = 'No active terminal';
      } else if (enhanced) {
        icon = <Wand2 className="w-3.5 h-3.5" />;
        className += 'text-violet-400/70 hover:text-violet-300 hover:bg-base-content/10 cursor-pointer';
        title = 'Enhanced voice input — AI cleans up the transcript using project context';
      } else {
        icon = <Mic className="w-3.5 h-3.5" />;
        className += 'text-base-content/60 hover:text-base-content hover:bg-base-content/10 cursor-pointer';
        title = 'Voice input';
      }
      break;
  }

  return (
    <>
      <button
        onClick={disabled && state !== 'error' ? undefined : handleClick}
        disabled={disabled && state !== 'error'}
        className={className}
        title={title}
      >
        {icon}
        {state === 'recording' && (
          <span className="text-[10px] text-red-400 tabular-nums">{formatDuration(elapsed)}</span>
        )}
        {enhanced && state === 'idle' && !notConfigured && !noTerminal && (
          <span className="absolute -top-0.5 -right-0.5" aria-hidden>
            <Sparkles className="w-2 h-2 text-violet-400" />
          </span>
        )}
      </button>
      {(state === 'recording' || finishing) && (liveCommitted || liveProvisional) && (
        <LiveTranscriptOverlay committed={liveCommitted} provisional={liveProvisional} finishing={finishing} />
      )}
    </>
  );
}

/**
 * Floating overlay shown during LA-2 live transcription. Renders the
 * decoder's committed text in normal weight and the provisional tail
 * in italics + 60% opacity — the "wobble then settle" UX the LocalAgreement-2
 * decoder produces. Hidden in batch / local-rotating-chunks modes (those
 * paths don't surface intermediate text, by design).
 *
 * Portaled to document.body so the floating position isn't constrained by
 * any FlexLayout/tabset overflow rules; sits bottom-right above the
 * status footer so it's out of the way of the terminal text the user is
 * about to overwrite.
 */
function LiveTranscriptOverlay({ committed, provisional, finishing }: { committed: string; provisional: string; finishing?: boolean }) {
  return createPortal(
    <div
      className="fixed bottom-12 right-4 z-[9000] max-w-md max-h-48 overflow-y-auto rounded-lg border border-base-300 bg-base-100/95 px-4 py-3 text-sm shadow-2xl backdrop-blur-sm"
      role="status"
      aria-live="polite"
    >
      <div className="mb-1 flex items-center gap-2 text-[10px] uppercase tracking-wider text-base-content/50">
        <span className={`inline-block h-1.5 w-1.5 rounded-full ${finishing ? 'animate-spin bg-amber-400' : 'animate-pulse bg-red-400'}`} />
        {finishing ? 'Finishing…' : 'Live transcription'}
      </div>
      <div className="whitespace-pre-wrap break-words leading-snug">
        <span className="text-stone-100">{committed}</span>
        {committed && provisional ? ' ' : ''}
        <span className="italic text-base-content/70">{provisional}</span>
      </div>
    </div>,
    document.body,
  );
}
